import { useEffect, useState } from 'react';

// Instalar la app en el celular. Mismo molde que Soltura.
//
// Android (Chrome) avisa con el evento `beforeinstallprompt`, y la app puede
// mostrar su propio botón "Instalar". iPhone no tiene ese evento: Apple no deja
// que una página se instale sola, así que lo único posible es explicar dónde
// tocar (Compartir → Agregar a inicio).

/** Si se pospone, no se vuelve a ofrecer por este tiempo. */
const POSPONER_MS = 14 * 24 * 60 * 60 * 1000;
const CLAVE_POSPUESTO = 'torneos.instalarPospuesto';

interface EventoInstalar extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type EstadoInstalar =
  | { tipo: 'oculto' }
  | { tipo: 'android'; instalar: () => Promise<void>; posponer: () => void }
  | { tipo: 'iphone'; posponer: () => void };

function pospuesto(): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(CLAVE_POSPUESTO) ?? 0) < POSPONER_MS;
  } catch {
    return false;
  }
}

export function esIphone(): boolean {
  const ua = navigator.userAgent;
  // El iPad moderno dice ser una Mac: se lo reconoce porque tiene pantalla táctil.
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

/** Ya está abierta como app instalada (no en el navegador). */
export function estaInstalada(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function useInstalar(): EstadoInstalar {
  const [evento, setEvento] = useState<EventoInstalar | null>(null);
  const [oculto, setOculto] = useState(() => estaInstalada() || pospuesto());

  useEffect(() => {
    const capturar = (e: Event) => {
      // Sin esto, Chrome muestra su propia barrita y el botón de la app sobra.
      e.preventDefault();
      setEvento(e as EventoInstalar);
    };
    const instalada = () => setOculto(true);
    window.addEventListener('beforeinstallprompt', capturar);
    window.addEventListener('appinstalled', instalada);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturar);
      window.removeEventListener('appinstalled', instalada);
    };
  }, []);

  if (oculto) return { tipo: 'oculto' };

  const posponer = () => {
    try {
      localStorage.setItem(CLAVE_POSPUESTO, String(Date.now()));
    } catch {
      // Sin almacenamiento: se oculta igual, solo que vuelve la próxima vez.
    }
    setOculto(true);
  };

  if (evento) {
    return {
      tipo: 'android',
      posponer,
      instalar: async () => {
        await evento.prompt();
        const { outcome } = await evento.userChoice;
        if (outcome === 'accepted') setOculto(true);
        else posponer();
      },
    };
  }

  if (esIphone()) return { tipo: 'iphone', posponer };
  return { tipo: 'oculto' };
}

